// Painting one display-list item's appearance on Canvas 2D (DRAW_PLAN §1 "Appearance"). D1: solid fills and
// strokes, opacity, blend; D2 adds stroke alignment, arrowheads, dashes in full; D6 gradients and effects.
import type { ArrowId, ColorRef, Fill, Paint, Stroke, BlendMode, Doc } from '../model/types';
import { geometry, type Item } from './displayList';
import { drawImage, drawText } from './adPaint';

const COMPOSITE: Record<BlendMode, GlobalCompositeOperation> = {
  normal: 'source-over',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  'color-dodge': 'color-dodge',
  'color-burn': 'color-burn',
  'hard-light': 'hard-light',
  'soft-light': 'soft-light',
  difference: 'difference',
  exclusion: 'exclusion',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
};
export const composite = (b: BlendMode) => COMPOSITE[b] ?? 'source-over';

/** Naive CMYK → RGB for on-screen preview (DRAW_PLAN §7: labelled approximate). */
export const cmykToRgb = (c: number, m: number, y: number, k: number): [number, number, number] => [
  (1 - c) * (1 - k),
  (1 - m) * (1 - k),
  (1 - y) * (1 - k),
];

/** Resolves a colour (global swatches, CMYK, spot tint) to sRGB 0..1 + alpha. */
export function resolveColor(
  c: ColorRef,
  doc?: Doc | null,
): { rgb: [number, number, number]; alpha: number } {
  let ref = c;
  if (c.swatchId && doc) {
    const sw = doc.swatches.find((s) => s.id === c.swatchId);
    if (sw) ref = { ...sw.color, alpha: c.alpha };
  }
  if (ref.rgb) return { rgb: ref.rgb, alpha: ref.alpha };
  if (ref.cmyk) {
    const [cc, m, y, k] = ref.cmyk;
    const t = ref.spot ? ref.spot.tint : 1;
    return { rgb: cmykToRgb(cc * t, m * t, y * t, k * t), alpha: ref.alpha };
  }
  return { rgb: [0, 0, 0], alpha: ref.alpha };
}

export function cssColor(c: ColorRef, doc?: Doc | null, extraAlpha = 1): string {
  const { rgb, alpha } = resolveColor(c, doc);
  return `rgba(${Math.round(rgb[0] * 255)},${Math.round(rgb[1] * 255)},${Math.round(rgb[2] * 255)},${alpha * extraAlpha})`;
}

/** A canvas fill/stroke style for a paint (gradients and patterns arrive in D6). */
export function paintStyle(
  g: CanvasRenderingContext2D,
  p: Paint,
  doc: Doc | null,
): string | CanvasGradient | null {
  if (p.kind === 'solid') return cssColor(p.color, doc);
  if (p.kind === 'linear' || p.kind === 'radial') {
    const gr =
      p.kind === 'linear'
        ? g.createLinearGradient(p.geom.x1, p.geom.y1, p.geom.x2, p.geom.y2)
        : g.createRadialGradient(
            p.geom.x1,
            p.geom.y1,
            0,
            p.geom.x1,
            p.geom.y1,
            p.geom.r ?? Math.hypot(p.geom.x2 - p.geom.x1, p.geom.y2 - p.geom.y1),
          );
    for (const s of p.stops) gr.addColorStop(Math.min(1, Math.max(0, s.offset)), cssColor(s.color, doc));
    return gr;
  }
  return null;
}

export function applyStroke(g: CanvasRenderingContext2D, s: Stroke, widthScale = 1) {
  g.lineWidth = s.width * widthScale;
  g.lineCap = s.cap;
  g.lineJoin = s.join;
  g.miterLimit = s.miter;
  g.setLineDash(s.dash);
  g.lineDashOffset = s.dashOffset;
}

/** Draws an item (its world matrix must not be on the context yet). Object opacity and blend apply to the
 *  object as a whole (Illustrator): with several paints, the object is drawn into a layer first. */
export function paintItem(g: CanvasRenderingContext2D, it: Item, doc: Doc | null) {
  const n = it.node;
  if (!it.path) return;
  const a = n.appearance;
  if (n.kind === 'text' || n.kind === 'image') {
    // M14: one paint (glyphs) or a clipped image; opacity and blend apply to the whole object.
    g.save();
    g.globalAlpha *= a.opacity;
    g.globalCompositeOperation = composite(a.blend);
    g.transform(...it.world);
    if (n.kind === 'text') drawText(g, n, it.path, doc);
    else drawImage(g, n, it.path, doc);
    g.restore();
    return;
  }
  const paints = a.fills.length + a.strokes.length;
  const isolated = (a.opacity < 1 || a.blend !== 'normal') && paints > 1;
  if (!isolated) {
    g.save();
    g.globalAlpha *= a.opacity;
    g.globalCompositeOperation = composite(a.blend);
    g.transform(...it.world);
    drawPaints(g, it, doc);
    g.restore();
    return;
  }
  // Layer the size of the item's device bounds (clipped to the target).
  const m = g.getTransform();
  const corners = [
    [it.bounds.x, it.bounds.y],
    [it.bounds.x + it.bounds.w, it.bounds.y],
    [it.bounds.x, it.bounds.y + it.bounds.h],
    [it.bounds.x + it.bounds.w, it.bounds.y + it.bounds.h],
  ].map(([x, y]) => [m.a * x! + m.c * y! + m.e, m.b * x! + m.d * y! + m.f]);
  const W = g.canvas.width,
    H = g.canvas.height;
  const x0 = Math.max(0, Math.floor(Math.min(...corners.map((c) => c[0]!))) - 2),
    y0 = Math.max(0, Math.floor(Math.min(...corners.map((c) => c[1]!))) - 2),
    x1 = Math.min(W, Math.ceil(Math.max(...corners.map((c) => c[0]!))) + 2),
    y1 = Math.min(H, Math.ceil(Math.max(...corners.map((c) => c[1]!))) + 2);
  if (x1 <= x0 || y1 <= y0) return;
  const layer = new OffscreenCanvas(x1 - x0, y1 - y0);
  const lg = layer.getContext('2d')! as unknown as CanvasRenderingContext2D;
  lg.setTransform(m.a, m.b, m.c, m.d, m.e - x0, m.f - y0);
  lg.transform(...it.world);
  drawPaints(lg, it, doc);
  g.save();
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.globalAlpha *= a.opacity;
  g.globalCompositeOperation = composite(a.blend);
  g.drawImage(layer, x0, y0);
  g.restore();
}

/** Fills, then strokes (with alignment and arrowheads), in the item's own coordinates. */
function drawPaints(g: CanvasRenderingContext2D, it: Item, doc: Doc | null) {
  const n = it.node;
  const path = it.path!;
  const rule: CanvasFillRule = n.kind === 'path' ? n.fillRule : 'nonzero';
  const closed = isClosed(n);
  for (const f of n.appearance.fills as Fill[]) {
    const st = paintStyle(g, f.paint, doc);
    if (!st) continue;
    g.save();
    g.globalAlpha *= f.opacity;
    g.globalCompositeOperation = composite(f.blend);
    g.fillStyle = st;
    g.fill(path, rule);
    g.restore();
  }
  for (const s of n.appearance.strokes) {
    const st = paintStyle(g, s.paint, doc);
    if (!st || s.width <= 0) continue;
    g.save();
    g.globalAlpha *= s.opacity;
    g.globalCompositeOperation = composite(s.blend);
    g.strokeStyle = st;
    g.fillStyle = st;
    // Inside / outside only make sense on closed paths (Illustrator too): a double-width stroke, clipped.
    const align = closed ? s.align : 'center';
    applyStroke(g, s, align === 'center' ? 1 : 2);
    if (align === 'inside') g.clip(path, rule);
    else if (align === 'outside') {
      const outside = new Path2D();
      outside.rect(-1e7, -1e7, 2e7, 2e7);
      outside.addPath(path);
      g.clip(outside, 'evenodd');
    }
    g.stroke(path);
    if (!closed && ((s.startArrow && s.startArrow !== 'none') || (s.endArrow && s.endArrow !== 'none'))) {
      g.setLineDash([]);
      for (const [end, kind] of [
        ['start', s.startArrow],
        ['end', s.endArrow],
      ] as const)
        if (kind && kind !== 'none') drawArrow(g, it, end, kind, s.width);
    }
    g.restore();
  }
}

const isClosed = (n: Item['node']) => {
  if (n.kind === 'shape') return n.shape.type !== 'line';
  if (n.kind === 'path') return n.subpaths.every((sp) => sp.closed);
  return true;
};

/** Ends of the first open subpath: the point and the direction pointing out of the path. */
export function pathEnds(
  it: Item,
): { start: [number, number, number]; end: [number, number, number] } | null {
  const sps = geometry(it.node).subpaths.filter((sp) => !sp.closed && sp.points.length >= 2);
  const sp = sps[0];
  if (!sp) return null;
  const p = sp.points;
  const a = p[0]!,
    b = p[1]!,
    y = p[p.length - 1]!,
    z = p[p.length - 2]!;
  // Tangent from the handle when there is one, else from the neighbouring anchor.
  const sx = a.outX !== a.x || a.outY !== a.y ? a.outX : b.x,
    sy = a.outX !== a.x || a.outY !== a.y ? a.outY : b.y;
  const ex = y.inX !== y.x || y.inY !== y.y ? y.inX : z.x,
    ey = y.inX !== y.x || y.inY !== y.y ? y.inY : z.y;
  return {
    start: [a.x, a.y, Math.atan2(a.y - sy, a.x - sx)],
    end: [y.x, y.y, Math.atan2(y.y - ey, y.x - ex)],
  };
}

/** Arrowheads (DRAW_PLAN §1): sized from the stroke width (triangle 4.5 widths long). */
function drawArrow(g: CanvasRenderingContext2D, it: Item, which: 'start' | 'end', kind: ArrowId, w: number) {
  const ends = pathEnds(it);
  if (!ends) return;
  const [x, y, ang] = ends[which];
  const L = Math.max(w, 0.5) * 4.5;
  g.save();
  g.translate(x, y);
  g.rotate(ang);
  g.beginPath();
  switch (kind) {
    case 'triangle':
      g.moveTo(L * 0.25, 0);
      g.lineTo(-L * 0.75, L / 2);
      g.lineTo(-L * 0.75, -L / 2);
      g.closePath();
      g.fill();
      break;
    case 'open':
      g.lineWidth = w;
      g.lineJoin = 'miter';
      g.moveTo(-L * 0.75, L / 2);
      g.lineTo(0, 0);
      g.lineTo(-L * 0.75, -L / 2);
      g.stroke();
      break;
    case 'circle':
      g.arc(0, 0, L / 3, 0, Math.PI * 2);
      g.fill();
      break;
    case 'square':
      g.rect(-L / 3, -L / 3, (2 * L) / 3, (2 * L) / 3);
      g.fill();
      break;
    case 'bar':
      g.rect(-w / 2, -L / 2, w, L);
      g.fill();
      break;
  }
  g.restore();
}
